import React, { DOMAttributes } from 'react';
import classnames from 'classnames';
import { useDrag, useDrop } from 'react-dnd';
import styles from './drag-sort.scss';
import { DropResult } from './index';
import { useAriaLive, MessageValues } from 'hellospa/common/hooks/use-aria-live';
import { defineMessages } from 'react-intl';
import { ValuesOfRecord } from 'hellospa/ts-utils';

export const messages = defineMessages({
  itemPickedUp: {
    id: '',
    defaultMessage: 'Press up or down to change order of {type} {name}',
    description: 'Press up or down to change order of (signer) {name}',
  },
  itemMoved: {
    id: '',
    defaultMessage: 'Moved {type} {name} to position {position}. Press space to deselect.',
    description: 'Screen reader announcement when an item is moved',
  },
  dragHandleFocused: {
    id: '',
    defaultMessage: '{type} {name} focused, position (pos). Press space to select and reorder.',
    description: 'Screen reader lablel for the drag handle',
  },
});

type MessageValue = ValuesOfRecord<NonNullable<MessageValues>>

export type Config<TData> = {
  type: string,
  lastIndex: number,
  onDrop: (result: DropResult<TData>) => void
  messages: typeof messages,
  messageValues: (item: TData) => {
    name: MessageValue,
    type: MessageValue,
  }
}
type ItemContext<TData> = {
  dragRef: undefined | React.RefObject<HTMLDivElement>,
  grab: (grabbed: boolean) => void,
  item: TData,
}


// I tried to use unknown here, but dragSorterContext.Provider complains that TData
// can't be assigned to unknown.
export const dragSortContext = React.createContext<Config<any>>({
  type: '',
  lastIndex: 0,
  onDrop: () => { },
  messages,
  messageValues: () => ({
    name: 'NAME',
    type: 'TYPE',
  }),
});
export const dragItemContext = React.createContext<ItemContext<any>>({
  dragRef: undefined,
  grab: () => undefined,
  item: {},
});


export type DragItemType = {
  type: string,
  index: number,
  position: {
    x: number,
    y: number,
    width: number,
    height: number,
  }
  content: React.ReactNode,
}


type DragProps = {
  isDragging: boolean,
}

type DragItemProps<TData> = {
  index: number,
  children: React.ReactNode
  item: TData
}


type ContainerProps = {
  itemRef: React.RefObject<HTMLDivElement>,
  isGrabbed: boolean,
  grab: (isGrabbed: boolean) => void,
  children: React.ReactNode,

  onKeyDown?: DOMAttributes<HTMLDivElement>['onKeyDown'],
  className?: string,
  style?: React.CSSProperties,
}
// eslint-disable-next-line react/display-name
export const ItemContainer = React.memo(
  function ItemContainer({
    itemRef,
    isGrabbed,
    grab,
    onKeyDown,
    className,
    children,
    style = {},
  }: ContainerProps) {
    return (
      <div
        ref={itemRef}
        // -1 allows the item to become focused, but doesn't place it in the tab
        // order. The undefined prevents allowing the user to focus the div by
        // clicking it.
        tabIndex={isGrabbed ? -1 : undefined}
        onBlur={(e) => {
          // If the focus is moved, drop the item. This needs to check
          // `e.target`, because without it this picks up the blur event of
          // <ItemHandle and drops the item before the focus has been moved.
          if (isGrabbed && itemRef.current && itemRef.current === e.target) {
            grab(false);
          }
        }}
        onKeyDown={onKeyDown}
        style={style}
        className={classnames(styles.itemContainer, className)}
      >
        {children}
      </div>
    );
  },

);


export function DragItem<TData>({ index, children, item }: DragItemProps<TData>) {
  const itemRef = React.useRef<HTMLDivElement>(null);
  const dragRef = React.useRef<HTMLDivElement>(null);
  const [isGrabbed, setGrabbed] = React.useState(false);
  // TS just has to trust me that the context we're getting is a TData
  const config = React.useContext<Config<TData>>(dragSortContext);
  const announce = useAriaLive();

  const grab = React.useCallback((value: boolean) => {
    if (value === isGrabbed) {
      return;
    }

    if (value) {
      announce(config.messages.itemPickedUp, {
        ...config.messageValues(item),
        position: index + 1,
      });
    }
    setGrabbed(value);
  }, [announce, config, index, isGrabbed, item]);

  React.useEffect(function manageFocus() {
    if (isGrabbed && itemRef.current) {
      itemRef.current.focus();
    }
    // When the item is dropped, push the focus back to the drag handle
    if (!isGrabbed && dragRef.current && itemRef.current) {
      // When the item is dropped its tabIndex goes back to `undefined` and the
      // focus is moved back to document.body. I don't want to steal focus if
      // the user tabbed away or focused something else.
      if (document.activeElement && document.activeElement === document.body) {
        dragRef.current.focus();
      }
    }
  }, [isGrabbed]);

  const [{ isDragging }, drag] = useDrag<DragItemType, DropResult<TData>, DragProps>({
    item: {
      type: config.type,
      index,
      // This needs to be a valid object, but it will always get replaced with
      // `begin()`
      position: { x: 0, y: 0, height: 0, width: 0 },
      content: <div />,
    },
    begin: (): DragItemType => {
      const boundingBox = itemRef.current!.getBoundingClientRect();

      return {
        type: config.type,
        index,
        // react-dnd captures the initial position of the drag handle, but it
        // doesn't capture the initial position of the whole item.
        position: {
          x: boundingBox.left,
          y: boundingBox.top,
          width: boundingBox.width,
          height: boundingBox.height,
        },
        content: children,
      };
    },
    collect: (monitor) => {
      return {
        isDragging: monitor.isDragging(),
        isOver: monitor.getTargetIds(),
      };
    },
    end: (_a, monitor) => {
      if (monitor.didDrop()) {
        const result: DropResult<TData> = monitor.getDropResult();
        config.onDrop(result);
      }
    },
  });
  type DropProps = {
    hoveringItem: null | DragItemType;
  };
  const [{ hoveringItem }, drop] = useDrop<DragItemType, DropResult<TData>, DropProps>({
    accept: config.type,
    collect: (monitor) => {
      return {
        hoveringItem: monitor.isOver() ? monitor.getItem() : null,
      };
    },
    drop(dragItem) {
      return {
        item,
        from: dragItem.index,
        to: index,
      };
    },
  });
  drop(itemRef);
  drag(dragRef);

  const onKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isGrabbed) { return; }

    switch (e.key) {
      case ' ':
        grab(false);
        break;
      case 'ArrowUp':
        if (index > 0) {
          config.onDrop({
            item,
            from: index,
            to: index - 1,
          });
        }
        break;
      case 'ArrowDown':
        if (index < config.lastIndex) {
          config.onDrop({
            item,
            from: index,
            to: index + 1,
          });
        }
        break;
      default:
    }

  }, [config, grab, index, isGrabbed, item]);

  const contextValue = React.useMemo(() => ({
    dragRef,
    grab,
    item,
  }), [grab, item]);


  // This needs to be done after all the hooks
  if (!config.type) {
    throw new Error('DragItem must be rendered inside a DragSort');
  }
  return (
    <dragItemContext.Provider value={contextValue}>
      <ItemContainer
        itemRef={itemRef}
        isGrabbed={isGrabbed}
        grab={grab}
        onKeyDown={onKeyDown}
        className={classnames(styles.itemContainer, {
          [styles.isDragging]: isDragging,
          [styles.above]: hoveringItem && index < hoveringItem.index,
          [styles.below]: hoveringItem && index > hoveringItem.index,
        })}
      >
        {children}
      </ItemContainer>
    </dragItemContext.Provider>
  );
}
